export type PlaybackRecoveryState = {
  phase: 'primary' | 'original' | 'failed'
  source: string
  startTime: number
  attempt: number
}

function isHlsSource(source: string): boolean {
  try {
    return new URL(source).pathname.toLowerCase().endsWith('.m3u8')
  } catch {
    return false
  }
}

function isSignedOriginal(source?: string | null): source is string {
  try {
    const url = new URL(source ?? '')
    return (
      url.protocol === 'https:' &&
      url.hostname === 'download.vanishingincmagic.com' &&
      url.pathname.toLowerCase().endsWith('.mp4') &&
      !!url.searchParams.get('token') &&
      !!url.searchParams.get('expires')
    )
  } catch {
    return false
  }
}

type RecoveryOptions = {
  source: string
  originalSource?: string | null
  startTime: number
  readPosition: () => number
  onChange: (state: PlaybackRecoveryState) => void
}

/** Switch once on a reported HLS failure, using the original URL already authorized by the API. */
export function createPlaybackRecovery(options: RecoveryOptions) {
  let primarySource = options.source
  let original = selectOriginal(options.source, options.originalSource)
  let state: PlaybackRecoveryState = {
    phase: 'primary',
    source: options.source,
    startTime: options.startTime,
    attempt: 0,
  }
  let disposed = false

  function publish(next: PlaybackRecoveryState) {
    if (disposed) return
    state = next
    options.onChange(state)
  }

  function position() {
    const value = options.readPosition()
    return Number.isFinite(value) && value >= 0 ? value : state.startTime
  }

  return {
    get state() {
      return state
    },
    // A query refresh may rotate signatures for the same file. Keep the current
    // provider running, but use the refreshed URLs on the next recovery attempt.
    updateSources(source: string, originalSource?: string | null) {
      if (disposed) return
      primarySource = source
      original = selectOriginal(source, originalSource)
    },
    error() {
      if (disposed || state.phase === 'failed') return
      if (state.phase === 'primary' && isHlsSource(state.source) && original) {
        publish({
          phase: 'original',
          source: original,
          startTime: position(),
          attempt: state.attempt + 1,
        })
      } else {
        publish({ ...state, phase: 'failed', startTime: position() })
      }
    },
    retry() {
      if (disposed || state.phase !== 'failed') return
      publish({
        phase: original ? 'original' : 'primary',
        source: original ?? primarySource,
        startTime: position(),
        attempt: state.attempt + 1,
      })
    },
    dispose() {
      disposed = true
    },
  }
}

function selectOriginal(source: string, originalSource?: string | null) {
  return isHlsSource(source) && isSignedOriginal(originalSource)
    ? originalSource
    : null
}

export type PlaybackRecovery = ReturnType<typeof createPlaybackRecovery>
